import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Calendar, MessageSquare, Users, Key, Eye, EyeOff, Shield, ShieldOff, Lock, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcademyMyKids({ user }) {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Password dialog state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      const response = await axios.get(`${API}/academy/my-children`, {
        withCredentials: true
      });
      setChildren(response.data);
    } catch (error) {
      console.error("Error fetching children:", error);
      toast.error("Failed to load children");
    } finally {
      setLoading(false);
    }
  };

  // Enable login for a child
  const handleEnableLogin = async (childId) => {
    try {
      await axios.post(`${API}/academy/player/enable-login`, 
        { player_id: childId },
        { withCredentials: true }
      );
      toast.success("Login enabled! Now set a password for your child.");
      fetchChildren();
      // Open password dialog
      const child = children.find(c => c.id === childId);
      setSelectedChild(child);
      setShowPasswordDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to enable login");
    }
  };

  // Disable login for a child
  const handleDisableLogin = async (childId) => {
    if (!window.confirm("Disable login for this child? They won't be able to access their player dashboard.")) {
      return;
    }
    try {
      await axios.post(`${API}/academy/player/disable-login`, 
        { player_id: childId },
        { withCredentials: true }
      );
      toast.success("Login disabled");
      fetchChildren();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to disable login");
    }
  };

  // Open password dialog
  const openSetPasswordDialog = (child) => {
    setSelectedChild(child);
    setNewPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowPasswordDialog(true);
  };

  // Set password for child
  const handleSetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    try {
      setSettingPassword(true);
      await axios.post(`${API}/academy/player/set-password`, 
        { player_id: selectedChild.id, new_password: newPassword },
        { withCredentials: true }
      );
      toast.success("Password set successfully! Your child can now log in.");
      setShowPasswordDialog(false);
      setNewPassword("");
      setConfirmPassword("");
      fetchChildren();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set password");
    } finally {
      setSettingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/academy">
            <Button variant="ghost" className="text-white hover:text-blue-200">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Academy
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-white uppercase" style={{ fontFamily: "'Times New Roman', Times, Georgia, serif", letterSpacing: "0.05em" }}>SCC Football Academy</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <h2 className="text-4xl font-bold text-white mb-8">My Kids</h2>

        {children.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-slate-400">No children registered yet.</p>
          </Card>
        ) : (
          <div className="space-y-8">
            {children.map((child) => (
              <div key={child.id}>
                {/* Child Header */}
                <div className="mb-4">
                  <h3 className="text-3xl font-bold text-white">{child.child_name}</h3>
                  <p className="text-sm text-slate-400">Membership: {child.membership_number}</p>
                  {child.playing_position && (
                    <p className="text-sm text-slate-400">Position: {child.playing_position}</p>
                  )}
                </div>

                {/* Teams - One card per team */}
                {child.teams && child.teams.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {child.teams.map((team) => (
                      <Card key={team.id} className="p-6 bg-slate-900/50 border-2 border-blue-500/30 hover:border-blue-500 transition-all">
                        <div className="mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-5 h-5 text-blue-400" />
                            <h4 className="text-xl font-bold text-blue-300">{team.name}</h4>
                          </div>
                          {team.age_group && (
                            <p className="text-sm text-slate-400">Age Group: {team.age_group}</p>
                          )}
                        </div>

                        {/* Team Stats - Will be controlled by admin settings */}
                        {team.show_stats !== false && (
                          <div className="mb-4">
                            <p className="text-xs font-semibold text-slate-300 mb-2">Performance</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="p-2 bg-blue-500/10 rounded text-center">
                                <p className="text-lg font-bold text-blue-300">{team.games_played || 0}</p>
                                <p className="text-xs text-slate-400">Played</p>
                              </div>
                              <div className="p-2 bg-emerald-500/10 rounded text-center">
                                <p className="text-lg font-bold text-green-300">{team.games_won || 0}</p>
                                <p className="text-xs text-slate-400">Won</p>
                              </div>
                              <div className="p-2 bg-red-500/10 rounded text-center">
                                <p className="text-lg font-bold text-red-300">{team.games_lost || 0}</p>
                                <p className="text-xs text-slate-400">Lost</p>
                              </div>
                              <div className="p-2 bg-yellow-500/10 rounded text-center">
                                <p className="text-lg font-bold text-yellow-300">{team.games_draw || 0}</p>
                                <p className="text-xs text-slate-400">Draw</p>
                              </div>
                            </div>

                            {/* Optional: Goals/Assists (if team enables it) */}
                            {team.show_individual_stats && (
                              <div className="grid grid-cols-3 gap-2 mt-3">
                                <div className="p-2 bg-purple-500/10 rounded text-center">
                                  <p className="text-lg font-bold text-purple-300">{team.goals || 0}</p>
                                  <p className="text-xs text-slate-400">Goals</p>
                                </div>
                                <div className="p-2 bg-indigo-500/10 rounded text-center">
                                  <p className="text-lg font-bold text-indigo-300">{team.assists || 0}</p>
                                  <p className="text-xs text-slate-400">Assists</p>
                                </div>
                                <div className="p-2 bg-pink-500/10 rounded text-center">
                                  <p className="text-lg font-bold text-pink-300">{team.motm || 0}</p>
                                  <p className="text-xs text-slate-400">MOTM</p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2">
                          <Link to={`/academy/teams/${team.id}`} className="flex-1">
                            <Button size="sm" variant="outline" className="w-full">
                              <Users className="w-4 h-4 mr-1" />
                              Team
                            </Button>
                          </Link>
                          <Link to={`/academy/sessions?team_id=${team.id}`} className="flex-1">
                            <Button size="sm" variant="outline" className="w-full">
                              <Calendar className="w-4 h-4 mr-1" />
                              Team Sessions
                            </Button>
                          </Link>
                          <Link to={`/academy/player/${child.id}/feedback?team_id=${team.id}`} className="flex-1">
                            <Button size="sm" variant="outline" className="w-full border-blue-500/30 text-blue-400">
                              <MessageSquare className="w-4 h-4 mr-1" />
                              Feedback
                            </Button>
                          </Link>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card className="p-8 text-center">
                    <p className="text-slate-400">Not assigned to any team yet</p>
                  </Card>
                )}

                {/* Enrolled Sessions - What child signed up for */}
                {child.enrolled_sessions && child.enrolled_sessions.length > 0 && (
                  <Card className="mt-4 p-4 bg-emerald-500/10 border border-green-500/30">
                    <h4 className="text-lg font-bold text-emerald-400 mb-3 flex items-center">
                      <Calendar className="w-5 h-5 mr-2" />
                      Enrolled Weekly Sessions
                    </h4>
                    <div className="space-y-2">
                      {child.enrolled_sessions.map((session, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-900/50 rounded border border-green-500/30">
                          <div>
                            <span className="font-semibold text-white">{session.day_name}</span>
                            <span className="text-slate-400 ml-2">{session.start_time} - {session.end_time}</span>
                          </div>
                          <div className="text-sm text-slate-400">
                            {session.location_name || session.location}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Enrollment Info */}
                {child.enrollment && (
                  <Card className="mt-4 p-4 bg-[#0a0f1a] border border-slate-700">
                    <p className="text-sm font-semibold text-slate-300">
                      Package: 
                      {child.enrollment.package_type === 'package_a' && ' 1 session/week'}
                      {child.enrollment.package_type === 'package_b' && ' 2 sessions/week'}
                      {child.enrollment.package_type === 'package_c' && ' 2 sessions/week + Games'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Payment: {child.enrollment.paid ? '✅ Paid' : '⏳ Pending'}
                      {child.enrollment.paid && ` - $${child.enrollment.final_amount.toFixed(2)}`}
                    </p>
                  </Card>
                )}

                {/* Kid Login Management */}
                <Card className="mt-4 p-4 bg-slate-800/50 border border-slate-700">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-bold text-white flex items-center">
                      <Key className="w-5 h-5 mr-2 text-amber-400" />
                      Kid Login Access
                    </h4>
                    {child.login_enabled ? (
                      <span className="flex items-center gap-1 text-sm text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">
                        <Shield className="w-4 h-4" />
                        Enabled
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-sm text-slate-400 bg-slate-700/50 px-2 py-1 rounded">
                        <ShieldOff className="w-4 h-4" />
                        Disabled
                      </span>
                    )}
                  </div>
                  
                  <p className="text-sm text-slate-400 mb-4">
                    {child.login_enabled 
                      ? "Your child can log in using their membership number and password to view their schedule."
                      : "Enable login to let your child access their own player dashboard with schedule and events."}
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    {child.login_enabled ? (
                      <>
                        <Button
                          onClick={() => openSetPasswordDialog(child)}
                          variant="outline"
                          size="sm"
                          className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                        >
                          <Lock className="w-4 h-4 mr-1" />
                          {child.has_password ? "Reset Password" : "Set Password"}
                        </Button>
                        <Button
                          onClick={() => handleDisableLogin(child.id)}
                          variant="outline"
                          size="sm"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <ShieldOff className="w-4 h-4 mr-1" />
                          Disable Login
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => handleEnableLogin(child.id)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        size="sm"
                      >
                        <Shield className="w-4 h-4 mr-1" />
                        Enable Kid Login
                      </Button>
                    )}
                  </div>
                  
                  {/* Login instructions when enabled */}
                  {child.login_enabled && child.has_password && (
                    <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <p className="text-sm text-blue-300 font-medium mb-1">Login Instructions:</p>
                      <ul className="text-xs text-slate-400 space-y-1">
                        <li>• Go to the login page and enter:</li>
                        <li className="pl-4"><strong className="text-white">Username:</strong> {child.membership_number}</li>
                        <li className="pl-4"><strong className="text-white">Password:</strong> (the password you set)</li>
                        <li>• Your child will see their schedule, games, and events</li>
                      </ul>
                    </div>
                  )}
                  
                  {/* Prompt to set password */}
                  {child.login_enabled && !child.has_password && (
                    <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <p className="text-sm text-amber-300">
                        ⚠️ Login is enabled but no password set. Click "Set Password" above to allow your child to log in.
                      </p>
                    </div>
                  )}
                </Card>

                <hr className="my-8 border-slate-700" />
              </div>
            ))}
          </div>
        )}
        
        {/* Password Dialog */}
        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-400" />
                Set Password for {selectedChild?.child_name}
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Create a password your child will use to log in with their membership number ({selectedChild?.membership_number})
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSetPassword} className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-300">New Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className="bg-slate-800 border-slate-600 text-white pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Choose something your child can remember (e.g., favorite color + number)
                </p>
              </div>
              
              <div>
                <Label className="text-slate-300">Confirm Password</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  className="bg-slate-800 border-slate-600 text-white"
                  required
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPasswordDialog(false)}
                  className="border-slate-600 text-slate-300"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={settingPassword}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {settingPassword ? "Setting..." : "Set Password"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
